// Pure prior-draft-vs-new-draft diff used by the verbal-authoring chat
// refinement panel. Produces a short human-readable summary like
// `Renamed "8X10" → "8X10-LOFT"; removed line "EMT 1/2in"` that the
// chat renders beneath each assistant turn.
//
// The diff is best-effort — it names the obvious changes, not every
// nested field tweak. If two drafts differ in ways the helper doesn't
// recognize, it falls back to `Updated draft`. Plan reference:
// `docs/planning/mfg_recipe_verbal_authoring.md` §3.4.

import type { DictatedRecipeDraft, DictatedDraftLine } from "@/lib/manufacturing/types/recipeDictation";

const MAX_SUMMARY_PARTS = 6;

function quoted(s: string): string {
  return `"${s}"`;
}

function lineMaterialKey(line: DictatedDraftLine): string {
  if (line.match.kind === "matched") return line.match.canonical_name.toLowerCase();
  return line.material_name_heard.trim().toLowerCase();
}

function describeLine(line: DictatedDraftLine): string {
  const name =
    line.match.kind === "matched" ? line.match.canonical_name : line.material_name_heard;
  return quoted(name.trim() || "(unnamed)");
}

function metaChangeParts(prior: DictatedRecipeDraft, next: DictatedRecipeDraft): string[] {
  const parts: string[] = [];
  if (prior.code !== next.code) {
    if (!prior.code && next.code) parts.push(`Set code to ${quoted(next.code)}`);
    else if (prior.code && !next.code) parts.push(`Cleared code (was ${quoted(prior.code)})`);
    else parts.push(`Renamed code ${quoted(prior.code)} → ${quoted(next.code)}`);
  }
  if (prior.name !== next.name) {
    if (!prior.name && next.name) parts.push(`Set name to ${quoted(next.name)}`);
    else if (prior.name && !next.name) parts.push(`Cleared name`);
    else parts.push(`Renamed ${quoted(prior.name)} → ${quoted(next.name)}`);
  }
  if (prior.kind !== next.kind) {
    parts.push(`Changed kind ${quoted(prior.kind)} → ${quoted(next.kind)}`);
  }
  const tagSet = (tags: string[]) => new Set(tags.map((t) => t.toLowerCase()));
  const priorTags = tagSet(prior.tags);
  const nextTags = tagSet(next.tags);
  const added: string[] = [];
  const removed: string[] = [];
  for (const t of nextTags) if (!priorTags.has(t)) added.push(t);
  for (const t of priorTags) if (!nextTags.has(t)) removed.push(t);
  if (added.length > 0) parts.push(`Added tag${added.length > 1 ? "s" : ""} ${added.map(quoted).join(", ")}`);
  if (removed.length > 0) parts.push(`Removed tag${removed.length > 1 ? "s" : ""} ${removed.map(quoted).join(", ")}`);
  return parts;
}

type LineDiff = {
  added: DictatedDraftLine[];
  removed: DictatedDraftLine[];
  edited: Array<{ before: DictatedDraftLine; after: DictatedDraftLine }>;
};

function diffLines(prior: DictatedDraftLine[], next: DictatedDraftLine[]): LineDiff {
  const out: LineDiff = { added: [], removed: [], edited: [] };
  const priorByKey = new Map<string, DictatedDraftLine>();
  for (const p of prior) priorByKey.set(lineMaterialKey(p), p);
  const nextByKey = new Map<string, DictatedDraftLine>();
  for (const n of next) nextByKey.set(lineMaterialKey(n), n);

  for (const [key, p] of priorByKey) {
    const n = nextByKey.get(key);
    if (!n) {
      out.removed.push(p);
      continue;
    }
    if (!linesEqual(p, n)) out.edited.push({ before: p, after: n });
  }
  for (const [key, n] of nextByKey) {
    if (!priorByKey.has(key)) out.added.push(n);
  }
  return out;
}

function linesEqual(a: DictatedDraftLine, b: DictatedDraftLine): boolean {
  return (
    a.quantity === b.quantity &&
    a.role === b.role &&
    a.slot_label === b.slot_label &&
    a.upgrade_price_delta === b.upgrade_price_delta &&
    a.match.kind === b.match.kind
  );
}

function lineChangeParts(prior: DictatedRecipeDraft, next: DictatedRecipeDraft): string[] {
  const { added, removed, edited } = diffLines(prior.lines, next.lines);
  const parts: string[] = [];
  if (added.length === 1) parts.push(`Added line ${describeLine(added[0])}`);
  else if (added.length > 1) parts.push(`Added ${added.length} lines`);
  if (removed.length === 1) parts.push(`Removed line ${describeLine(removed[0])}`);
  else if (removed.length > 1) parts.push(`Removed ${removed.length} lines`);
  for (const e of edited) {
    const label = describeLine(e.after);
    if (e.before.quantity !== e.after.quantity) {
      parts.push(`Changed ${label} quantity ${e.before.quantity} → ${e.after.quantity}`);
    } else if (e.before.role !== e.after.role) {
      parts.push(`Changed ${label} role ${quoted(e.before.role)} → ${quoted(e.after.role)}`);
    } else if (e.before.upgrade_price_delta !== e.after.upgrade_price_delta) {
      parts.push(
        `Changed ${label} upgrade delta ${e.before.upgrade_price_delta ?? "null"} → ${e.after.upgrade_price_delta ?? "null"}`
      );
    } else if (e.before.slot_label !== e.after.slot_label) {
      parts.push(`Changed ${label} slot ${quoted(e.before.slot_label)} → ${quoted(e.after.slot_label)}`);
    }
  }
  return parts;
}

/** Produce a single-line, human-readable summary of how `next` differs
 *  from `prior`. Returns `"Updated draft"` when nothing recognizable
 *  changed, so callers always have a non-empty string to render. */
export function summarizeDictatedRecipeDraftDiff(
  prior: DictatedRecipeDraft,
  next: DictatedRecipeDraft
): string {
  const parts = [...metaChangeParts(prior, next), ...lineChangeParts(prior, next)];
  if (parts.length === 0) return "Updated draft";
  const capped = parts.slice(0, MAX_SUMMARY_PARTS);
  const suffix = parts.length > MAX_SUMMARY_PARTS ? `; +${parts.length - MAX_SUMMARY_PARTS} more` : "";
  return capped.join("; ") + suffix;
}
